<template>
  <SidenavElement
    :itemClass="itemClasses"
    :to="routeTo"
    :isActive="isActive"
    :text="useDefaultSlot ? undefined : company.name"
    :adminId="adminId ?? null"
    :hasAction="hasAction"
    :showActions="hasAction"
  >
    <template v-if="useDefaultSlot" #default>
      <div class="company-entry__content">
        <span class="text-break">{{ company.name }}</span>
      </div>
    </template>

    <template #actions>
      <div v-if="hasAction" class="sidenav-action-pill">
        <FollowUpButton :state="followupState" buttonClass="action-pill-btn" />
      </div>
    </template>
  </SidenavElement>
</template>

<script setup lang="ts">
import SidenavElement from "@/components/layout/SidenavElement.vue";
import FollowUpButton from "@/dashboard/common/FollowUpButton.vue";
import type { FollowupState } from "@/composables/useFollowupState";
import type { Company } from "@/types";
import { computed } from "vue";
import type { RouteLocationRaw } from "vue-router";

const props = withDefaults(
  defineProps<{
    company: Company;
    baseItemClass: Array<string | Record<string, boolean>>;
    isActive: boolean;
    hasAction: boolean;
    followupState: FollowupState;
    adminId?: number | null;
    nested?: boolean;
    useDefaultSlot?: boolean;
  }>(),
  {
    adminId: null,
    nested: false,
    useDefaultSlot: true,
  }
);

const itemClasses = computed(() =>
  props.nested
    ? [...props.baseItemClass, "company-entry--nested"]
    : props.baseItemClass
);

const routeTo = computed<RouteLocationRaw | undefined>(() =>
  props.company.id
    ? {
        path: "/dashboard",
        query: {
          company_id: props.company.id,
          consolidated: "false",
        },
      }
    : undefined
);
</script>

<style scoped lang="scss">
.sidenav-action-pill {
  display: flex;
}

:deep(.action-pill-btn) {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.4;
  text-decoration: none;
  border-radius: 0.375rem;
  background: rgba(var(--phoenix-orange-50-rgb, 245, 210, 110), 0.18);
  border: 1px solid rgba(var(--phoenix-orange-50-rgb, 245, 210, 110), 0.45);
  color: var(--phoenix-orange-50);
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;

  &::before {
    content: "";
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background-color: var(--phoenix-orange-50);
    flex-shrink: 0;
  }

  svg {
    display: none;
  }

  &:hover {
    background: rgba(var(--phoenix-orange-50-rgb, 245, 210, 110), 0.28);
    border-color: rgba(var(--phoenix-orange-50-rgb, 245, 210, 110), 0.6);
    color: var(--phoenix-orange-50);
    text-decoration: none;
  }
}
</style>
